import { useParams, Link } from "react-router-dom";

import { Chip } from "@/components/Chip";
import { DataTable, type DataCol } from "@/components/DataTable";
import { KV } from "@/components/KV";
import { PageHead } from "@/components/PageHead";
import { StatGrid } from "@/components/StatGrid";
import {
  ROLE_LABEL, useOrgUsers, useOrganisation, type AllocationRow, type OrgUserRow,
} from "@/modules/organisations/data";

/** One organisation: what it holds and who works in it.
 *
 *  REAL DATA. This page rendered `detail_fixtures.ts`, which is where "Nexcell
 *  line 2" came from — a space in a company that does not exist, allocated to
 *  a product ("Warranty Insights") that does not exist either.
 *
 *  AN ALLOCATION is `(company, product, space)`: the thing a person is
 *  actually granted, and the thing a tool enters. The product is a catalogue
 *  KIND since D-92, and the space belongs to that kind (D-89) — so what
 *  differs between companies is never WHICH spaces exist, only which they
 *  hold and what may be done in them.
 *
 *  404 MEANS BOTH "no such organisation" AND "not yours", deliberately. A
 *  distinct answer would tell a customer that CMP-014 is real.
 */
const ALLOC_COLS: DataCol<AllocationRow>[] = [
  { key: "kind", label: "Product", width: 140, mono: true, render: (a) => a.kind },
  { key: "space", label: "Space", width: 180, render: (a) => a.custom_name ?? a.space },
  { key: "slug", label: "Slug", width: 160, mono: true, render: (a) => a.space_slug },
  {
    key: "users", label: "People with access", width: 150,
    render: (a) => a.users_with_access,
  },
  {
    key: "enabled", label: "State", width: 110,
    render: (a) => a.enabled
      ? <Chip tone="ok">enabled</Chip>
      : <Chip tone="mute">disabled</Chip>,
  },
];

const USER_COLS: DataCol<OrgUserRow>[] = [
  { key: "name", label: "Name", width: 170, render: (u) => u.name },
  { key: "login", label: "Login", width: 230, mono: true, render: (u) => u.login_id },
  { key: "role", label: "Role", width: 140, render: (u) => ROLE_LABEL[u.role] ?? u.role },
  {
    key: "mfa", label: "MFA", width: 80,
    render: (u) => u.mfa ? <Chip tone="ok">on</Chip> : <Chip tone="warn">off</Chip>,
  },
  {
    key: "access", label: "Space access", width: 220,
    render: (u) => u.space_access.length
      ? u.space_access.map((a) => `${a.kind}/${a.space}`).join(", ")
      : <span className="cap">none</span>,
  },
];

export function OrganisationDetailPage() {
  const { orgId = "" } = useParams();
  const q = useOrganisation(orgId);
  const people = useOrgUsers(orgId);

  if (q.isLoading) {
    return <div className="mpage on"><div className="panel panel-pad cap">Loading…</div></div>;
  }
  if (q.isError) {
    return (
      <div className="mpage on">
        <div className="panel panel-pad">
          No such organisation, or it is not one you may see.{" "}
          <Link to="/organisations/organisations">Back to organisations</Link>
        </div>
      </div>
    );
  }

  const o = q.data!;
  const users = people.data?.users ?? [];

  return (
    <div className="mpage on">
      <PageHead
        eyebrow="Organisations"
        title={o.name}
        desc={`${o.organisation_id} · ${o.plan}`}
      />

      <div className="panel panel-pad" style={{ marginBottom: 12 }}>
        <div style={{ display: "flex", gap: 28, flexWrap: "wrap" }}>
          <KV k="Organisation ID" v={o.organisation_id} mono />
          <KV k="Plan" v={o.plan} />
          <KV k="Status" v={o.status} />
        </div>
      </div>

      <StatGrid stats={[
        { k: "Allocations", v: o.allocations.length },
        { k: "People", v: users.length },
        { k: "With MFA", v: users.filter((u) => u.mfa).length },
      ]} />

      <h3 className="cap" style={{ marginTop: 16 }}>Allocated spaces</h3>
      <DataTable rows={o.allocations} cols={ALLOC_COLS} rowKey={(a) => a.cps_id}
        empty={<div className="panel panel-pad cap">
          This organisation holds no product spaces yet.
        </div>} />

      <h3 className="cap" style={{ marginTop: 16 }}>People</h3>
      <DataTable rows={users} cols={USER_COLS} rowKey={(u) => u.user_id}
        empty={<div className="panel panel-pad cap">
          {people.isLoading ? "Loading…" : "Nobody in this organisation yet."}
        </div>} />
    </div>
  );
}
